/**
 * AdminGate component
 * Protects routes that require admin access
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { isAdmin } from '@/utils/admin';
import { Loader2 } from 'lucide-react';

interface AdminGateProps {
  children: React.ReactNode;
}

export default function AdminGate({ children }: AdminGateProps) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [adminLoading, setAdminLoading] = useState(true);
  const [isUserAdmin, setIsUserAdmin] = useState(false);

  useEffect(() => {
    const checkAdmin = async () => {
      if (authLoading) {
        console.log('[AdminGate] Waiting for auth to load...');
        return;
      }

      if (!user) {
        console.log('[AdminGate] No user, redirecting to login');
        navigate('/login');
        return;
      }

      console.log('[AdminGate] Checking admin status for:', user.email);
      const admin = await isAdmin(user);
      console.log('[AdminGate] Admin check result:', admin);
      
      setIsUserAdmin(admin);
      setAdminLoading(false);

      if (!admin) {
        console.warn('[AdminGate] User is not admin, redirecting to dashboard');
        navigate('/dashboard');
      } else {
        console.log('[AdminGate] User is admin, allowing access');
      }
    };

    checkAdmin();
  }, [user, authLoading, navigate]);

  if (authLoading || adminLoading) {
    return (
      <div className="min-h-screen bg-sl-bg flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-sl-accent" />
      </div>
    );
  }

  if (!isUserAdmin) {
    return null; // Will redirect
  }

  return <>{children}</>;
}

